import React from 'react';
import Button from './Button';
import './DuplicateAlertModal.css';

export interface DuplicateMatch {
  id: string;
  name?: string;
  website?: string | null;
  type?: string | null;
  phone?: string | null;
  billing_city?: string | null;
  billing_state?: string | null;
  owner_name?: string | null;
  url: string;
  match_confidence?: number | null;
}

export interface DuplicateAlertPayload {
  message: string;
  rule: string;
  // If false, the SF rule is Block-mode — "Create anyway" can't override it.
  allow_save: boolean;
  duplicates: DuplicateMatch[];
}

interface Props {
  alert: DuplicateAlertPayload;
  /** What the rep was trying to create — shown so they can compare. */
  attemptedName: string;
  attemptedWebsite?: string;
  /** Re-POSTs the create with allow_save=true. Disabled if rule is Block-mode. */
  onCreateAnyway: () => void;
  onCancel: () => void;
  /** True while the "Create anyway" re-POST is in flight. */
  saving?: boolean;
}

/**
 * Surfaces Salesforce's DUPLICATES_DETECTED response to the rep — what got
 * matched, why, and what to do about it. Replaces the old "Salesforce create
 * failed (502)" toast that hid the real reason.
 */
const DuplicateAlertModal: React.FC<Props> = ({
  alert,
  attemptedName,
  attemptedWebsite,
  onCreateAnyway,
  onCancel,
  saving = false,
}) => {
  const blocked = !alert.allow_save;
  return (
    <div className="dup-alert-backdrop" onClick={onCancel}>
      <div className="dup-alert-modal" onClick={(e) => e.stopPropagation()}>
        <header className="dup-alert-header">
          <h2>Possible duplicate in Salesforce</h2>
          <button type="button" className="dup-alert-close" onClick={onCancel} aria-label="Close">×</button>
        </header>

        <p className="dup-alert-explainer">
          Salesforce {blocked ? 'blocked' : 'flagged'} this create — its{' '}
          <code>{alert.rule || 'duplicate rule'}</code> thinks{' '}
          <strong>{attemptedName}</strong>{attemptedWebsite ? ` (${attemptedWebsite})` : ''}{' '}
          looks like {alert.duplicates.length === 1 ? 'this existing Account' : 'these existing Accounts'}:
        </p>

        <ul className="dup-alert-list">
          {alert.duplicates.map((d) => (
            <li key={d.id} className="dup-alert-row">
              <div className="dup-alert-row-main">
                <a href={d.url} target="_blank" rel="noopener noreferrer" className="dup-alert-row-name">
                  {d.name || '(no name)'}
                </a>
                {typeof d.match_confidence === 'number' && (
                  <span className="dup-alert-row-confidence">{Math.round(d.match_confidence)}% match</span>
                )}
              </div>
              <div className="dup-alert-row-meta">
                {d.type && <span className="dup-alert-row-pill">{d.type}</span>}
                {d.owner_name && <span>Owner: {d.owner_name}</span>}
                {(d.billing_city || d.billing_state) && (
                  <span>{[d.billing_city, d.billing_state].filter(Boolean).join(', ')}</span>
                )}
                {d.website && <span className="dup-alert-row-site">{d.website}</span>}
              </div>
            </li>
          ))}
        </ul>

        {blocked && (
          <p className="dup-alert-block-warning">
            ⚠ This rule is in <strong>Block mode</strong> — you can’t override it from here.
            Open the existing record (or change Type in Salesforce) if appropriate.
          </p>
        )}

        <footer className="dup-alert-footer">
          <Button variant="secondary" onClick={onCancel} type="button">Cancel</Button>
          <Button
            variant="primary"
            type="button"
            onClick={onCreateAnyway}
            disabled={blocked || saving}
            loading={saving}
            title={blocked ? 'Rule is Block mode — override not allowed' : 'Create the new Account anyway'}
          >
            Create anyway
          </Button>
        </footer>
      </div>
    </div>
  );
};

export default DuplicateAlertModal;
